import { useEffect, useState, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth, useTheme } from "@/App";
import { asArray } from "@/lib/arrays";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  BookOpen,
  PlayCircle,
  Clock,
  CheckCircle,
  ChevronRight,
  FileText,
  Lock,
  ShieldCheck,
  Maximize2,
  Minimize2,
  X,
  Search,
  ExternalLink
} from "lucide-react";
import { toast } from "sonner";

const MODULE_DURATION_MINUTES = 60;

const getProtectedViewerUrl = (rawUrl) => {
  if (!rawUrl) return "";
  let url = rawUrl.trim();
  if (url.includes("drive.google.com/file/d/")) {
    return url.replace(/\/(view|edit)(\?.*)?$/, "/preview");
  }
  if (url.includes("docs.google.com") && !url.includes("/preview")) {
    return url.replace(/\/edit(\?.*)?$/, "/preview");
  }
  if (url.toLowerCase().includes(".pdf") || url.includes("/uploads/")) {
    const cleanUrl = url.split("#")[0];
    return `${cleanUrl}#toolbar=0&navpanes=0&scrollbar=1`;
  }
  return url;
};

const getLessonDocs = (lesson) => {
  if (!lesson) return [];
  const docs = [];
  if (lesson.notes_url) {
    docs.push({
      name: lesson.notes_title || `${lesson.title} - Notes`,
      url: lesson.notes_url,
      type: lesson.notes_type || "notes",
      lessonTitle: lesson.title,
      lessonId: lesson.lesson_id,
    });
  }
  if (Array.isArray(lesson.resources)) {
    lesson.resources.forEach((r, idx) => {
      if (r?.url && r.url !== lesson.notes_url) {
        docs.push({
          name: r.name || r.title || `${lesson.title} - Resource ${idx + 1}`,
          url: r.url,
          type: r.type || "resource",
          lessonTitle: lesson.title,
          lessonId: lesson.lesson_id,
        });
      }
    });
  }
  return docs;
};

const CoursePage = () => {
  const { courseId } = useParams();
  const { user } = useAuth();
  const { theme } = useTheme();
  const isLight = theme === "light";

  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [lessons, setLessons] = useState({});
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  // PDF Viewer & All-PDFs Directory States
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [isFullscreenDoc, setIsFullscreenDoc] = useState(false);
  const [showAllPdfsModal, setShowAllPdfsModal] = useState(false);
  const [pdfSearchQuery, setPdfSearchQuery] = useState("");

  useEffect(() => {
    if (user?.role !== 'admin' && !(user?.enrolled_courses || []).includes(courseId)) {
      setLoading(false);
      return;
    }
    fetchCourseData();
  }, [courseId, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle ESC key to close reader/directory modals
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (selectedDoc) setSelectedDoc(null);
        else if (showAllPdfsModal) setShowAllPdfsModal(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedDoc, showAllPdfsModal]);

  const fetchCourseData = async () => {
    try {
      const courseRes = await axios.get(`${API}/courses/${courseId}`, { withCredentials: true });
      setCourse(courseRes.data);
      const mods = asArray(courseRes.data?.modules_data);
      setModules(mods);

      // Fetch progress + all module lessons in parallel
      const [progressRes, ...lessonsResults] = await Promise.all([
        axios.get(`${API}/progress/${courseId}`, { withCredentials: true }),
        ...mods.map(m => axios.get(`${API}/lessons/${m.module_id}`, { withCredentials: true }))
      ]);
      setProgress(progressRes.data);

      const lessonsMap = {};
      mods.forEach((m, i) => { lessonsMap[m.module_id] = asArray(lessonsResults[i].data, "lessons"); });
      setLessons(lessonsMap);
    } catch (error) {
      toast.error("Failed to load course");
    } finally {
      setLoading(false);
    }
  };

  const isLessonCompleted = (lessonId) => {
    return progress?.completed_lessons?.includes(lessonId);
  };

  const getModuleProgress = (moduleId) => {
    const moduleLessons = lessons[moduleId] || [];
    if (moduleLessons.length === 0) return 0;
    const completed = moduleLessons.filter((l) => isLessonCompleted(l.lesson_id)).length;
    return Math.round((completed / moduleLessons.length) * 100);
  };

  // Compile all PDFs attached across all modules & lessons in this course
  const allCoursePdfs = useMemo(() => {
    const list = [];
    modules.forEach((mod, modIdx) => {
      const modLessons = lessons[mod.module_id] || [];
      modLessons.forEach((l, lIdx) => {
        const docs = getLessonDocs(l);
        docs.forEach((doc) => {
          list.push({
            ...doc,
            moduleId: mod.module_id,
            moduleTitle: mod.title,
            moduleOrder: modIdx + 1,
            lessonOrder: lIdx + 1,
          });
        });
      });
    });
    return list;
  }, [modules, lessons]);

  // Filtered PDFs based on search query
  const filteredPdfs = useMemo(() => {
    if (!pdfSearchQuery.trim()) return allCoursePdfs;
    const q = pdfSearchQuery.toLowerCase();
    return allCoursePdfs.filter(
      (doc) =>
        doc.name.toLowerCase().includes(q) ||
        doc.moduleTitle.toLowerCase().includes(q) ||
        doc.lessonTitle.toLowerCase().includes(q)
    );
  }, [allCoursePdfs, pdfSearchQuery]);

  if (loading) {
    return (
      <DashboardLayout title="Course">
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-[#00d991] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (user?.role !== 'admin' && !(user?.enrolled_courses || []).includes(courseId)) {
    return <Navigate to={`/courses/${courseId}`} replace />;
  }

  if (!course) {
    return (
      <DashboardLayout title="Course">
        <div className="text-center py-12">
          <p className="text-[#94A3B8]">Course not found</p>
        </div>
      </DashboardLayout>
    );
  }

  const allCourseLessons = Object.values(lessons).flat();
  const totalCompleted = allCourseLessons.filter(l => isLessonCompleted(l.lesson_id)).length;
  const progressPercentage = Math.round(progress?.progress_percentage || (allCourseLessons.length > 0 ? (totalCompleted / allCourseLessons.length) * 100 : 0));

  return (
    <DashboardLayout title={course.title}>
      <div className="space-y-6 max-w-7xl mx-auto" data-testid="course-page">
        {/* Course Header */}
        <Card className={`overflow-hidden border shadow-sm ${isLight ? "bg-white border-slate-200" : "bg-[#131B2C] border-[#1E293B]"}`} data-testid="course-header">
          <div className="relative">
            <img
              src={course.thumbnail || "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1200"}
              alt={course.title}
              className="w-full h-48 md:h-64 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
            
            <div className="absolute bottom-4 left-6 right-6 z-10">
              <h1 className="text-2xl md:text-3xl font-bold !text-white font-outfit mb-2 drop-shadow-md">
                {course.title}
              </h1>
              <p className="text-slate-200 text-sm max-w-3xl line-clamp-2 drop-shadow">
                {course.description}
              </p>
            </div>
          </div>
          
          <CardContent className="p-6">
            <div className="flex flex-wrap items-center gap-3 sm:gap-4">
              <Badge className={`border-0 font-jetbrains text-xs font-semibold ${isLight ? "bg-emerald-100 text-emerald-800" : "bg-[#00d991]/20 text-[#00d991]"}`}>
                <BookOpen className="w-3.5 h-3.5 mr-1" />
                {modules.length} Modules
              </Badge>
              <Badge className={`border-0 font-jetbrains text-xs font-semibold ${isLight ? "bg-sky-100 text-sky-800" : "bg-[#2563EB]/20 text-sky-400"}`}>
                <PlayCircle className="w-3.5 h-3.5 mr-1" />
                {allCourseLessons.length} Lessons
              </Badge>

              {allCoursePdfs.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowAllPdfsModal(true)}
                  className={`border-0 font-jetbrains text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1.5 transition-all hover:scale-105 cursor-pointer ${
                    isLight 
                      ? "bg-amber-100 text-amber-900 hover:bg-amber-200 border border-amber-300" 
                      : "bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/30"
                  }`}
                  title="Click to view all attached PDFs"
                  data-testid="badge-all-pdfs"
                >
                  <FileText className="w-3.5 h-3.5" />
                  {allCoursePdfs.length} Attached PDF{allCoursePdfs.length !== 1 ? "s" : ""}
                </button>
              )}

              <div className="flex items-center gap-3 flex-1 min-w-[200px]">
                <Progress
                  value={progressPercentage}
                  className={`h-2.5 flex-1 ${isLight ? "bg-slate-100" : "bg-[#1E293B]"}`}
                />
                <span className={`text-xs font-bold font-jetbrains ${isLight ? "text-emerald-700" : "text-[#00d991]"}`}>
                  {progressPercentage}%
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Modules Syllabus */}
        <div className="space-y-4" data-testid="modules-list">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <h2 className={`text-xl font-bold font-outfit ${isLight ? "text-slate-900" : "text-white"}`}>
                Course Content
              </h2>
              {allCoursePdfs.length > 0 && (
                <Badge className="hidden sm:inline-flex bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 text-xs font-mono font-medium items-center gap-1.5 px-2 py-0.5">
                  <FileText className="w-3 h-3" />
                  {allCoursePdfs.length} PDF{allCoursePdfs.length !== 1 ? "s" : ""}
                </Badge>
              )}
            </div>

            <div className="flex items-center gap-3">
              {allCoursePdfs.length > 0 && (
                <Button
                  onClick={() => setShowAllPdfsModal(true)}
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 shadow-sm transition-all hover:brightness-105 active:scale-95"
                  data-testid="all-pdfs-header-btn"
                >
                  <FileText className="w-3.5 h-3.5" />
                  <span>View Attached PDFs ({allCoursePdfs.length})</span>
                </Button>
              )}
              <span className={`text-xs font-jetbrains font-semibold ${isLight ? "text-slate-500" : "text-[#94A3B8]"}`}>
                {totalCompleted} of {allCourseLessons.length} completed
              </span>
            </div>
          </div>
          
          <Accordion type="single" collapsible className="space-y-3">
            {modules.map((module, idx) => {
              const modProgress = getModuleProgress(module.module_id);
              const isModComplete = modProgress === 100;
              const moduleLessonsList = lessons[module.module_id] || [];
              const modPdfs = moduleLessonsList.flatMap(getLessonDocs);

              return (
                <AccordionItem
                  key={module.module_id}
                  value={module.module_id}
                  className={`border rounded-xl overflow-hidden shadow-xs transition-colors ${
                    isLight ? "bg-white border-slate-200" : "bg-[#131B2C] border-[#1E293B]"
                  }`}
                  data-testid={`module-${module.module_id}`}
                >
                  <AccordionTrigger className={`px-5 py-4 hover:no-underline transition-colors ${
                    isLight ? "hover:bg-slate-50 text-slate-900" : "hover:bg-[#1A243A] text-white"
                  }`}>
                    <div className="flex items-center gap-4 flex-1">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold font-outfit text-sm shrink-0 ${
                        isModComplete
                          ? "bg-emerald-600 !text-white"
                          : isLight ? "bg-sky-100 text-sky-800" : "bg-[#00d991]/20 text-[#00d991]"
                      }`}>
                        {isModComplete ? <CheckCircle className="w-5 h-5" /> : idx + 1}
                      </div>

                      <div className="flex-1 text-left min-w-0">
                        <h3 className={`font-semibold font-outfit text-base truncate ${isLight ? "text-slate-900" : "text-white"}`}>
                          {module.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <p className={`text-xs font-jetbrains ${isLight ? "text-slate-500" : "text-[#64748B]"}`}>
                            {moduleLessonsList.length} lesson{moduleLessonsList.length !== 1 ? "s" : ""}
                          </p>
                          {modPdfs.length > 0 && (
                            <>
                              <span className="text-zinc-600">•</span>
                              <span className="inline-flex items-center gap-1 text-[11px] font-mono text-emerald-500 font-medium">
                                <FileText className="w-3 h-3" />
                                {modPdfs.length} PDF{modPdfs.length !== 1 ? "s" : ""}
                              </span>
                            </>
                          )}
                        </div>
                      </div>

                      {modPdfs.length > 0 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (modPdfs.length === 1) {
                              setSelectedDoc(modPdfs[0]);
                            } else {
                              setShowAllPdfsModal(true);
                            }
                          }}
                          className={`hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all mr-2 ${
                            isLight
                              ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200"
                              : "bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/30"
                          }`}
                          title={`Click to view attached PDF for ${module.title}`}
                        >
                          <FileText className="w-3.5 h-3.5" />
                          <span>View PDF{modPdfs.length !== 1 ? ` (${modPdfs.length})` : ""}</span>
                        </button>
                      )}

                      <div className="hidden sm:flex items-center gap-2 mr-4">
                        <Progress
                          value={modProgress}
                          className={`w-24 h-2 ${isLight ? "bg-slate-100" : "bg-[#1E293B]"}`}
                        />
                        <span className={`text-xs font-mono font-bold ${
                          isModComplete ? "text-emerald-600" : isLight ? "text-slate-600" : "text-[#94A3B8]"
                        }`}>
                          {modProgress}%
                        </span>
                      </div>
                    </div>
                  </AccordionTrigger>

                  <AccordionContent className={`border-t ${isLight ? "border-slate-100 bg-slate-50/30" : "border-[#1E293B]"}`}>
                    <div className={`divide-y ${isLight ? "divide-slate-100" : "divide-[#1E293B]"}`}>
                      {moduleLessonsList.map((lesson) => {
                        const done = isLessonCompleted(lesson.lesson_id);
                        const lessonDocs = getLessonDocs(lesson);

                        return (
                          <div
                            key={lesson.lesson_id}
                            className={`flex items-center justify-between gap-3 px-5 py-3.5 transition-colors ${
                              isLight ? "hover:bg-slate-100/70" : "hover:bg-[#1A243A]"
                            }`}
                            data-testid={`lesson-${lesson.lesson_id}`}
                          >
                            <Link
                              to={`/lesson/${lesson.lesson_id}`}
                              className="flex items-center gap-4 flex-1 min-w-0"
                            >
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                                done
                                  ? "bg-emerald-600 !text-white"
                                  : isLight ? "bg-slate-200 text-slate-600" : "bg-[#1E293B] text-[#64748B]"
                              }`}>
                                {done ? (
                                  <CheckCircle className="w-4 h-4" />
                                ) : (
                                  <PlayCircle className="w-4 h-4" />
                                )}
                              </div>

                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-outfit truncate ${
                                  done
                                    ? isLight ? "text-slate-500 font-medium" : "text-[#94A3B8]"
                                    : isLight ? "text-slate-900 font-bold" : "text-white"
                                }`}>
                                  {lesson.title}
                                </p>
                                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                  <span className={`text-xs font-jetbrains flex items-center gap-1 ${isLight ? "text-slate-400" : "text-[#64748B]"}`}>
                                    <Clock className="w-3 h-3" />
                                    {lesson.duration_minutes || MODULE_DURATION_MINUTES} min
                                  </span>
                                  {lessonDocs.length > 0 && (
                                    <>
                                      <span className="text-zinc-600">•</span>
                                      <span className="text-[11px] text-emerald-500 font-mono flex items-center gap-1 font-medium">
                                        <FileText className="w-3 h-3" />
                                        PDF Attached
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </Link>

                            <div className="flex items-center gap-2 shrink-0">
                              {/* Direct PDF View Button */}
                              {lessonDocs.map((doc, docIdx) => (
                                <Button
                                  key={doc.url || docIdx}
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSelectedDoc(doc);
                                  }}
                                  className={`h-8 px-2.5 rounded-lg text-xs font-medium font-mono flex items-center gap-1.5 transition-all shadow-xs ${
                                    isLight
                                      ? "bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200"
                                      : "bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 hover:text-emerald-300 border border-emerald-500/30"
                                  }`}
                                  title={`Open PDF: ${doc.name}`}
                                >
                                  <FileText className="w-3.5 h-3.5" />
                                  <span className="hidden sm:inline">View PDF</span>
                                </Button>
                              ))}

                              <Link
                                to={`/lesson/${lesson.lesson_id}`}
                                className={`p-1.5 rounded-lg hover:bg-white/5 transition-colors ${
                                  isLight ? "text-slate-400 hover:text-slate-600" : "text-[#64748B] hover:text-white"
                                }`}
                                title="Open Lesson Video"
                              >
                                <ChevronRight className="w-4 h-4" />
                              </Link>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>

        {/* ── All Attached PDFs Course Directory Modal ── */}
        {showAllPdfsModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-black/80 backdrop-blur-md animate-in fade-in duration-200"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowAllPdfsModal(false);
            }}
          >
            <div className="flex flex-col w-full max-w-3xl max-h-[85vh] bg-[#0D1117] border border-white/10 rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-slate-900/90 select-none">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 shrink-0">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white font-outfit">
                      Course Study Notes & PDFs
                    </h3>
                    <p className="text-xs text-zinc-400 font-mono">
                      {allCoursePdfs.length} document{allCoursePdfs.length !== 1 ? "s" : ""} attached to {course.title}
                    </p>
                  </div>
                </div>

                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowAllPdfsModal(false)}
                  className="text-zinc-400 hover:text-rose-400 h-8 w-8 p-0 rounded-lg hover:bg-rose-500/10"
                  title="Close"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              {/* Search Bar */}
              {allCoursePdfs.length > 3 && (
                <div className="p-4 border-b border-white/5 bg-[#161B22]/50">
                  <div className="relative">
                    <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input
                      value={pdfSearchQuery}
                      onChange={(e) => setPdfSearchQuery(e.target.value)}
                      placeholder="Search PDF notes or lesson names..."
                      className="pl-9 h-10 bg-black/30 border-white/10 text-white placeholder:text-zinc-500 text-xs rounded-xl focus:border-emerald-500"
                    />
                  </div>
                </div>
              )}

              {/* PDF List */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-2.5">
                {filteredPdfs.length === 0 ? (
                  <div className="text-center py-12 text-zinc-500 text-sm">
                    {pdfSearchQuery ? "No matching PDFs found." : "No PDFs attached to this course yet."}
                  </div>
                ) : (
                  filteredPdfs.map((doc, idx) => (
                    <div
                      key={doc.url || idx}
                      onClick={() => {
                        setShowAllPdfsModal(false);
                        setSelectedDoc(doc);
                      }}
                      className="flex items-center justify-between gap-3 p-3.5 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-emerald-500/[0.08] hover:border-emerald-500/30 cursor-pointer transition-all group"
                      role="button"
                      tabIndex={0}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 shrink-0 group-hover:scale-105 transition-transform">
                          <FileText className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-white group-hover:text-emerald-300 truncate transition-colors">
                            {doc.name}
                          </p>
                          <div className="flex items-center gap-2 text-[11px] text-zinc-400 font-mono mt-0.5">
                            <span className="text-zinc-300">Mod {doc.moduleOrder}: {doc.moduleTitle}</span>
                            <span>•</span>
                            <span className="text-zinc-500 truncate">{doc.lessonTitle}</span>
                          </div>
                        </div>
                      </div>

                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 px-3 rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 text-xs font-semibold font-mono flex items-center gap-1.5 shrink-0"
                      >
                        <FileText className="w-3.5 h-3.5" />
                        <span>Read</span>
                      </Button>
                    </div>
                  ))
                )}
              </div>

              {/* Modal Footer */}
              <div className="px-5 py-3 border-t border-white/10 bg-slate-900/60 flex items-center justify-between text-[11px] text-zinc-500 font-mono">
                <span className="flex items-center gap-1">
                  <Lock className="w-3 h-3 text-emerald-400" />
                  Protected In-App Document Reader
                </span>
                <span>Press ESC to close</span>
              </div>
            </div>
          </div>
        )}

        {/* ── Protected PDF / Document Reader Modal ── */}
        {selectedDoc && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 md:p-6 bg-black/85 backdrop-blur-md animate-in fade-in duration-200"
            onContextMenu={(e) => {
              e.preventDefault();
              toast.info("Protected study document: download is restricted.");
            }}
          >
            <div className={`flex flex-col w-full ${isFullscreenDoc ? "h-full rounded-none" : "h-[92vh] max-w-6xl rounded-2xl"} bg-[#0D1117] border border-white/10 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200`}>
              {/* Reader Header */}
              <div className="flex items-center justify-between px-4 sm:px-6 py-3.5 border-b border-white/10 bg-slate-900/95 select-none">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 shrink-0">
                    <FileText className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-white truncate">
                      {selectedDoc.name || "Course Study Material"}
                    </h3>
                    <div className="flex items-center gap-2 text-[10px] text-zinc-400 font-mono">
                      <span className="flex items-center gap-1 text-emerald-400 font-semibold">
                        <Lock className="w-3 h-3" /> Protected View-Only Mode
                      </span>
                      {selectedDoc.lessonTitle && (
                        <>
                          <span>•</span>
                          <span className="text-zinc-400 truncate">{selectedDoc.lessonTitle}</span>
                        </>
                      )}
                      <span>•</span>
                      <span className="text-zinc-500">Downloads Restricted</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <Badge className="hidden sm:flex bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[10px] font-mono font-medium gap-1">
                    <ShieldCheck className="w-3 h-3" /> DRM Shield Active
                  </Badge>

                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setIsFullscreenDoc(!isFullscreenDoc)}
                    className="text-zinc-400 hover:text-white h-8 w-8 p-0 rounded-lg hover:bg-white/5"
                    title={isFullscreenDoc ? "Exit Fullscreen" : "Fullscreen"}
                  >
                    {isFullscreenDoc ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                  </Button>

                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelectedDoc(null)}
                    className="text-zinc-400 hover:text-rose-400 h-8 w-8 p-0 rounded-lg hover:bg-rose-500/10"
                    title="Close Reader"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Reader Document Iframe */}
              <div className="relative flex-1 w-full h-full bg-[#161B22] overflow-hidden select-none">
                <iframe
                  src={getProtectedViewerUrl(selectedDoc.url)}
                  title={selectedDoc.name || "Study Notes Viewer"}
                  className="w-full h-full border-0 bg-white"
                  onContextMenu={(e) => e.preventDefault()}
                />
                {/* Security watermark footer */}
                <div className="absolute bottom-2.5 right-4 pointer-events-none select-none bg-black/80 backdrop-blur-xs border border-white/10 px-3 py-1 rounded-full text-[10px] font-mono text-zinc-400 flex items-center gap-1.5 shadow-lg">
                  <Lock className="w-3 h-3 text-emerald-400" />
                  <span>Jamadhan Academy • Confidential Student Copy</span>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
};

export default CoursePage;
